/**
 * API client for communicating with the EvidenX backend
 *
 * Features:
 * - Automatic token refresh on 401 errors
 * - Concurrent request handling during refresh
 * - Request retry after successful refresh
 */

import { API_BASE_URL } from './api-base-url'

// Token storage keys
const ACCESS_TOKEN_KEY = 'evidenx_access_token'
const REFRESH_TOKEN_KEY = 'evidenx_refresh_token'

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: { error: string; message: string; messageKey?: string; details?: Record<string, unknown> } | null
  ) {
    super(body?.message ?? statusText)
    this.name = 'ApiError'
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  get isConflict(): boolean {
    return this.status === 409
  }

  get isServiceUnavailable(): boolean {
    const SERVICE_UNAVAILABLE = 503
    return this.status === SERVICE_UNAVAILABLE
  }
}

/**
 * Token management
 */
export const tokenStorage = {
  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY)
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
  },

  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  },

  clearTokens(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },

  hasTokens(): boolean {
    return !!this.getAccessToken()
  },
}

// =============================================================================
// TOKEN REFRESH MECHANISM
// =============================================================================

/** Promise that resolves when token refresh completes (or null if not refreshing) */
let refreshPromise: Promise<boolean> | null = null

/** Callbacks to notify when user is logged out due to refresh failure */
const logoutListeners: Array<() => void> = []

/**
 * Register a callback to be called when user is logged out due to token refresh failure.
 * Returns an unsubscribe function.
 */
export function onAuthLogout(callback: () => void): () => void {
  logoutListeners.push(callback)
  return () => {
    const index = logoutListeners.indexOf(callback)
    if (index !== -1) {
      logoutListeners.splice(index, 1)
    }
  }
}

/**
 * Notify all listeners that the user has been logged out
 */
function notifyLogout(): void {
  logoutListeners.forEach((callback) => {
    try {
      callback()
    } catch {
      // Ignore errors in callbacks
    }
  })
}

/**
 * Attempt to refresh the access token using the refresh token.
 * Returns true if refresh succeeded, false otherwise.
 * This is called directly without going through fetchApi to avoid circular calls.
 */
async function attemptTokenRefresh(): Promise<boolean> {
  const currentRefreshToken = tokenStorage.getRefreshToken()

  if (!currentRefreshToken) {
    return false
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken: currentRefreshToken }),
    })

    if (!response.ok) {
      return false
    }

    const data = (await response.json()) as { accessToken: string; refreshToken: string }
    tokenStorage.setTokens(data.accessToken, data.refreshToken)
    return true
  } catch {
    return false
  }
}

/**
 * Handle token refresh with concurrency control.
 * Multiple requests failing at the same time will share a single refresh attempt.
 */
async function handleTokenRefresh(): Promise<boolean> {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    return refreshPromise
  }

  // Start a new refresh
  refreshPromise = attemptTokenRefresh().finally(() => {
    refreshPromise = null
  })

  const success = await refreshPromise

  if (!success) {
    // Refresh failed - clear tokens and notify listeners
    tokenStorage.clearTokens()
    notifyLogout()
  }

  return success
}

// =============================================================================
// FETCH API
// =============================================================================

interface FetchOptions extends RequestInit {
  skipAuth?: boolean
  /** Internal flag to prevent infinite retry loops */
  _isRetry?: boolean
}

function buildRequestHeaders(
  fetchOptions: RequestInit,
  skipAuth: boolean | undefined
): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers as Record<string, string> | undefined),
  }
  if (!skipAuth) {
    const token = tokenStorage.getAccessToken()
    if (token) headers.Authorization = `Bearer ${token}`
  }
  return headers
}

async function readErrorBody<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T
  } catch {
    return null
  }
}

async function throwApiError(response: Response): Promise<never> {
  const errorBody = await readErrorBody<{ error: string; message: string; messageKey?: string; details?: Record<string, unknown> }>(
    response
  )
  throw new ApiError(response.status, response.statusText, errorBody)
}

/**
 * Fetch wrapper with common configuration, auth handling, and automatic token refresh
 */
async function fetchApi<T>(endpoint: string, options?: FetchOptions): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  const { skipAuth, _isRetry, ...fetchOptions } = options ?? {}
  const headers = buildRequestHeaders(fetchOptions, skipAuth)

  const response = await fetch(url, { ...fetchOptions, headers })

  if (response.status === 204) return undefined as T

  if (response.status === 401 && !skipAuth && !_isRetry) {
    const refreshed = await handleTokenRefresh()
    if (refreshed) return fetchApi<T>(endpoint, { ...options, _isRetry: true })
    return throwApiError(response)
  }

  if (!response.ok) return throwApiError(response)

  return response.json() as Promise<T>
}

/**
 * API client with typed methods
 */
export const apiClient = {
  get: <T>(endpoint: string, options?: { skipAuth?: boolean }): Promise<T> =>
    fetchApi<T>(endpoint, { method: 'GET', ...options }),

  post: <T>(endpoint: string, body?: unknown, options?: { skipAuth?: boolean }): Promise<T> =>
    fetchApi<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : null,
      ...options,
    }),

  put: <T>(endpoint: string, body: unknown): Promise<T> =>
    fetchApi<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  patch: <T>(endpoint: string, body: unknown): Promise<T> =>
    fetchApi<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  delete: <T>(endpoint: string, options?: { data?: unknown }): Promise<T> =>
    fetchApi<T>(endpoint, {
      method: 'DELETE',
      body: options?.data ? JSON.stringify(options.data) : null,
    }),
}

/**
 * Update the current user's settings (JSONB shallow merge)
 */
export function updateMySettings(
  settings: Record<string, unknown>
): Promise<import('../types').User> {
  return apiClient.patch('/auth/me/settings', settings)
}
